import { Injectable, signal } from '@angular/core';
import {
  LENDEE_UNPAID_OVERVIEW_KEYS,
  PartnerOverviewMetricKeys,
  PartnerTileMetricDefinition,
  PartnerTileValueFormat,
  UNPAID_PARTNER_OVERVIEW_KEYS,
  UNPAID_PARTNER_OVERVIEW_FROM_VISIBLE_TABLE,
  UNPAID_PARTNER_OVERVIEW_PAYABLE_COUNT,
  UNPAID_PARTNER_TILE_METRICS,
} from './unpaid-partner-tile-metrics.config';

function metricDefinitionsFor(productKey: string): PartnerTileMetricDefinition[] {
  return UNPAID_PARTNER_TILE_METRICS[productKey] ?? [];
}

export interface PartnerTileMetricRow {
  label: string;
  /** Ready for the template; `null` shows em dash. */
  display: string | null;
}

export interface PartnerOverviewData {
  availableBalance: number;
  upcomingAmount: number;
  totalPaymentToPay: number;
  totalPaymentsCount: number;
  urgentPaymentsAmount: number;
  urgentPaymentsCount: number;
}

const EMPTY_ROWS: PartnerTileMetricRow[] = [];
const DEFAULT_OVERVIEW: PartnerOverviewData = {
  availableBalance: 0,
  upcomingAmount: 0,
  totalPaymentToPay: 0,
  totalPaymentsCount: 0,
  urgentPaymentsAmount: 0,
  urgentPaymentsCount: 0,
};

function pickValue(root: unknown, keyPath: string): unknown {
  if (root === null || root === undefined) {
    return undefined;
  }
  const segments = keyPath.split('.').map((s) => s.trim()).filter(Boolean);
  let cur: unknown = root;
  for (const seg of segments) {
    if (cur === null || cur === undefined || typeof cur !== 'object') {
      return undefined;
    }
    cur = (cur as Record<string, unknown>)[seg];
  }
  return cur;
}

function isPresent(raw: unknown): boolean {
  return raw !== undefined && raw !== null && raw !== '';
}

function formatTileValue(raw: unknown, format: PartnerTileValueFormat): string {
  if (format === 'text') {
    return String(raw);
  }
  const n = Number(raw);
  if (Number.isNaN(n)) {
    return String(raw);
  }
  if (format === 'number') {
    return n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  }
  return (
    '$' +
    n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  );
}

function toNumber(raw: unknown): number {
  if (raw === undefined || raw === null || raw === '') {
    return 0;
  }
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

function pickFirstValue(root: unknown, keyPaths: string[]): unknown {
  for (const key of keyPaths) {
    const value = pickValue(root, key);
    if (isPresent(value)) {
      return value;
    }
  }
  return undefined;
}

function overviewKeysFor(productKey: string): PartnerOverviewMetricKeys | undefined {
  return UNPAID_PARTNER_OVERVIEW_KEYS[productKey];
}

@Injectable({
  providedIn: 'root',
})
export class UnpaidPartnerTileSummaryService {
  private readonly state = signal<Record<string, PartnerTileMetricRow[]>>({});
  private readonly overviewState = signal<Record<string, PartnerOverviewData>>({});

  /** Read in templates so tiles refresh when any child calls {@link updateFromListResponse}. */
  readonly tiles = this.state.asReadonly();
  readonly overviewTiles = this.overviewState.asReadonly();

  /** Snapshot for TS (per product key). */
  rowsFor(productKey: string): PartnerTileMetricRow[] {
    return this.state()[productKey] ?? EMPTY_ROWS;
  }

  overviewFor(productKey: string): PartnerOverviewData {
    return this.overviewState()[productKey] ?? DEFAULT_OVERVIEW;
  }

  /** Reads {@link UNPAID_PARTNER_TILE_METRICS} for this partner and fills rows from a list API response body. */
  updateFromListResponse(productKey: string, res: unknown): void {
    const defs = metricDefinitionsFor(productKey);
    if (!defs.length) {
      this.state.update((prev) => {
        if (!(productKey in prev)) return prev;
        const next = { ...prev };
        delete next[productKey];
        return next;
      });
      return;
    }

    const rows: PartnerTileMetricRow[] = defs.map((def) => {
      let raw: unknown;
      for (const k of def.keys) {
        const v = pickValue(res, k);
        if (isPresent(v)) {
          raw = v;
          break;
        }
      }
      const format: PartnerTileValueFormat = def.format ?? 'currency';
      return {
        label: def.label,
        display: raw === undefined ? null : formatTileValue(raw, format),
      };
    });

    this.state.update((prev) => ({ ...prev, [productKey]: rows }));

    const overviewKeys = overviewKeysFor(productKey);
    if (!overviewKeys) {
      return;
    }
    const tableScopedOverview = UNPAID_PARTNER_OVERVIEW_FROM_VISIBLE_TABLE.has(productKey);
    const payableCountOverview = UNPAID_PARTNER_OVERVIEW_PAYABLE_COUNT.has(productKey);
    const currentOverview = tableScopedOverview || payableCountOverview ? this.overviewFor(productKey) : null;
    const overview: PartnerOverviewData = {
      availableBalance: toNumber(pickFirstValue(res, overviewKeys.availableBalance)),
      upcomingAmount: toNumber(pickFirstValue(res, overviewKeys.upcomingAmount)),
      totalPaymentToPay: tableScopedOverview
        ? (currentOverview?.totalPaymentToPay ?? 0)
        : toNumber(pickFirstValue(res, overviewKeys.totalPaymentToPay)),
      totalPaymentsCount:
        tableScopedOverview || payableCountOverview
          ? (currentOverview?.totalPaymentsCount ?? 0)
          : toNumber(pickFirstValue(res, overviewKeys.totalPaymentsCount)),
      urgentPaymentsAmount: tableScopedOverview
        ? (currentOverview?.urgentPaymentsAmount ?? 0)
        : toNumber(pickFirstValue(res, overviewKeys.urgentPaymentsAmount)),
      urgentPaymentsCount: tableScopedOverview
        ? (currentOverview?.urgentPaymentsCount ?? 0)
        : toNumber(pickFirstValue(res, overviewKeys.urgentPaymentsCount)),
    };
    this.overviewState.update((prev) => ({ ...prev, [productKey]: overview }));
  }

  /** Overview cards for `get_lendee_unpaid_list` (Credee filter 16 / FM filter 15). */
  updateLendeeOverviewFromListResponse(productKey: string, res: unknown): void {
    const keys = LENDEE_UNPAID_OVERVIEW_KEYS;
    const overview: PartnerOverviewData = {
      availableBalance: toNumber(pickFirstValue(res, keys.availableBalance)),
      upcomingAmount: toNumber(pickFirstValue(res, keys.upcomingAmount)),
      totalPaymentToPay: toNumber(pickFirstValue(res, keys.totalPaymentToPay)),
      totalPaymentsCount: toNumber(pickFirstValue(res, keys.totalPaymentsCount)),
      urgentPaymentsAmount: toNumber(pickFirstValue(res, keys.urgentPaymentsAmount)),
      urgentPaymentsCount: toNumber(pickFirstValue(res, keys.urgentPaymentsCount)),
    };
    this.overviewState.update((prev) => ({ ...prev, [productKey]: overview }));
  }

  patchOverview(productKey: string, patch: Partial<PartnerOverviewData>): void {
    const current = this.overviewFor(productKey);
    this.overviewState.update((prev) => ({
      ...prev,
      [productKey]: {
        ...current,
        ...patch,
      },
    }));
  }
}
